"use client";

import { useLang } from "@/contexts/language-context";
import AnimatedSection from "@/components/marketing/AnimatedSection";
import { Check, X } from "lucide-react";

// What you get with Redy's terminal and plans. No competitor columns: we only state what we can back up.

export default function HardwareCompareTable() {
  const { t } = useLang();

  const rows = [
    {
      label: { en: "Cost", es: "Costo" },
      sub: { en: "To get started", es: "Para empezar" },
      redy: { en: "$249 one-time", es: "$249 pago único" },
      redyClass: "text-redy-green font-black",
    },
    {
      label: { en: "You own it", es: "Lo posees" },
      sub: { en: "No monthly rental", es: "Sin renta mensual" },
      redy: true,
    },
    {
      label: { en: "No long-term contract", es: "Sin contrato a largo plazo" },
      sub: { en: "Month to month, 30 days' notice", es: "Mes a mes, aviso de 30 días" },
      redy: true,
    },
    {
      label: { en: "Offline cash mode", es: "Modo efectivo offline" },
      sub: { en: "Cash sales when WiFi drops", es: "Ventas en efectivo sin WiFi" },
      redy: true,
    },
    {
      label: { en: "Support included", es: "Soporte incluido" },
      sub: { en: "No extra charge", es: "Sin cargo extra" },
      redy: true,
    },
  ];

  const renderCell = (val: boolean | string | { en: string; es: string } | undefined, isRedy = false, note?: string) => {
    if (val === true) {
      return (
        <div className="flex justify-center">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isRedy ? "bg-redy-green/10" : "bg-black/5"}`}>
            <Check className={`w-3.5 h-3.5 stroke-[3] ${isRedy ? "text-redy-green" : "text-redy-charcoal"}`} />
          </div>
        </div>
      );
    }
    if (val === false) {
      return (
        <div className="flex flex-col items-center gap-1">
          <div className="w-6 h-6 rounded-full bg-black/4 flex items-center justify-center">
            <X className="w-3.5 h-3.5 text-redy-muted stroke-[3]" />
          </div>
          {note && <span className="text-[10px] font-bold text-redy-muted">{note}</span>}
        </div>
      );
    }
    if (typeof val === "object" && "en" in val) {
      return (
        <span className="text-[13px] font-bold text-redy-secondary">{t(val.en, val.es)}</span>
      );
    }
    return (
      <span className={`text-[14px] font-black ${isRedy ? "text-redy-green" : "text-redy-charcoal"}`}>
        {val as string}
      </span>
    );
  };

  return (
    <AnimatedSection className="py-16 px-6">
      <div className="max-w-[900px] mx-auto">
        <div className="text-center mb-10">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-3">
            {t("Compare", "Comparar")}
          </div>
          <h2 className="text-3xl md:text-[44px] font-black tracking-tight text-redy-charcoal">
            {t("Own it. Don't rent it.", "Poséelo. No lo rentes.")}
          </h2>
        </div>

        <div className="bg-white border border-black/5 rounded-[32px] overflow-hidden shadow-sm">
          {/* Header */}
          <div className="grid grid-cols-3 bg-redy-charcoal px-6 py-5">
            <div className="col-span-2 text-[12px] font-bold text-white/40">
              {t("Feature", "Función")}
            </div>
            <div className="col-span-1 text-[13px] font-black text-white text-center">Redy</div>
          </div>

          <div className="divide-y divide-black/5">
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-3 px-6 py-5 hover:bg-black/[0.015] transition-colors items-center">
                <div className="col-span-2">
                  <div className="text-[14px] font-bold text-redy-charcoal">
                    {t(row.label.en, row.label.es)}
                  </div>
                  <div className="text-[12px] text-redy-secondary mt-0.5">
                    {t(row.sub.en, row.sub.es)}
                  </div>
                </div>
                <div className="col-span-1 flex justify-center">{renderCell(row.redy, true)}</div>
              </div>
            ))}
          </div>

          <div className="px-6 py-4 border-t border-black/5 bg-[#FAFAFA]">
            <p className="text-[11px] font-medium text-redy-muted">
              {t("The terminal is a one-time purchase, set up with your Redy payments account. Software plans are month to month.", "El terminal es una compra única, configurada con tu cuenta de pagos de Redy. Los planes de software son mes a mes.")}
            </p>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}
